import { lazy, Suspense, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, Navigate, Route, Routes } from "react-router-dom";
import { type EnvName } from "./api";
import { RouteFallback } from "./components/RouteFallback";
import { cn } from "@/cn";
import { useAuth } from "./AuthContext";
import { useTheme } from "./ThemeProvider";
import { SimSidebar } from "./SimSidebar";
import { PttVoiceControl } from "./PttVoiceControl";
import { useApi } from "./useApi";
import { useBoardEvents } from "./useBoardEvents";
import { BoardAlertBar } from "./BoardAlertBar";
import { ExternalFeedsPanel } from "./ExternalFeedsPanel";
import { HashScroll } from "./HashScroll";
import { TransientsPanel } from "./TransientsPanel";

const BriefPage = lazy(() =>
  import("./BriefPage").then((m) => ({ default: m.BriefPage })),
);
const SectionPage = lazy(() =>
  import("./section").then((m) => ({ default: m.SectionPage })),
);
const BoardSummary = lazy(() =>
  import("./BoardSummary").then((m) => ({ default: m.BoardSummary })),
);
const OperationAdvisorPanel = lazy(() =>
  import("./OperationAdvisorPanel").then((m) => ({
    default: m.OperationAdvisorPanel,
  })),
);
const OperationsPage = lazy(() =>
  import("./OperationsPage").then((m) => ({ default: m.OperationsPage })),
);

const SECTIONS = ["WX", "AMOPS", "TOWER", "RAPCON", "RAWS"];

function EnvBanner({ env }: { env: EnvName }) {
  if (env === "sim") {
    return (
      <div className="env-banner env-banner--sim" role="status">
        <strong>Simulation mode</strong>
        <span>Training replay only: og_orm_sim database, not live operations</span>
        <Link to="/live" className="env-banner-link">
          Exit to live board
        </Link>
      </div>
    );
  }
  return (
    <div className="env-banner env-banner--live" role="status">
      <strong>Live operations</strong>
      <span>Ellsworth operational risk board</span>
    </div>
  );
}

function AppHeader({ env }: { env: EnvName }) {
  const prefix = `/${env}`;
  const { theme, toggle } = useTheme();
  const { enabled: authEnabled, user, logout } = useAuth();
  const isSim = env === "sim";
  return (
    <header className={cn("site-header", isSim && "site-header--sim")}>
      <div className="site-header-brand">
        <img src="/logo.png" alt="Tactical Edge" className="site-header-wordmark" />
        <span className="site-header-subtitle">Ellsworth ORM</span>
        <span
          className={cn(
            "site-header-badge",
            isSim ? "site-header-badge--sim" : "site-header-badge--live",
          )}
        >
          {isSim ? "Sim" : "Live"}
        </span>
      </div>
      <nav className="site-header-nav">
        <NavLink to={prefix} end className={({ isActive }) => (isActive ? "active" : "")}>
          Dashboard
        </NavLink>
        {SECTIONS.map((code) => (
          <NavLink
            key={code}
            to={`${prefix}/section/${code}`}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            {code}
          </NavLink>
        ))}
        <NavLink to={`${prefix}/brief`} className={({ isActive }) => (isActive ? "active" : "")}>
          Brief
        </NavLink>
        <NavLink to={`${prefix}/ops`} className={({ isActive }) => (isActive ? "active" : "")}>
          Ops
        </NavLink>
      </nav>
      <div className="site-header-controls">
        <PttVoiceControl env={env} />
        <button type="button" className="btn btn--hdr" onClick={toggle} title="Toggle dark mode">
          {theme === "dark" ? "☀ Light" : "☾ Dark"}
        </button>
        {authEnabled ? (
          <button
            type="button"
            className="btn btn--hdr"
            onClick={() => void logout()}
            title={user ? `Signed in as ${user}` : "Log out"}
          >
            Log out
          </button>
        ) : null}
        {isSim ? (
          <Link to="/live" className="btn btn--hdr">
            ← Live
          </Link>
        ) : (
          <Link to="/sim" className="btn btn--hdr">
            Sim →
          </Link>
        )}
      </div>
    </header>
  );
}

function DashboardPage({ env }: { env: EnvName }) {
  const api = useApi(env);
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", env],
    queryFn: api.dashboard,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  if (isLoading) return <p className="muted">Loading board…</p>;
  if (error) return <div className="error">{(error as Error).message}</div>;
  if (!data) return null;

  return (
    <>
      <Suspense fallback={<RouteFallback label="Loading board summary…" />}>
        <BoardSummary data={data} env={env} />
      </Suspense>
      <ExternalFeedsPanel env={env} compact />
      <Suspense fallback={<RouteFallback label="Loading advisor…" />}>
        <OperationAdvisorPanel
          env={env}
          blockingConflictCount={data.blocking_conflicts?.length ?? 0}
        />
      </Suspense>

      {(data.clocks?.length ?? 0) > 0 ? (
        <div className="card clocks">
          {(data.clocks ?? []).map((c) => (
            <div key={c.label} className="clock">
              <div className="label">{c.label}</div>
              <div className="value">{c.value}</div>
            </div>
          ))}
        </div>
      ) : null}

      {data.show_forecast || data.show_equipment ? (
        <div className="grid-2">
          {data.show_forecast ? (
            <div className="card">
              <h2 className="section-title">8-day forecast</h2>
              <div className="forecast-strip">
                {(data.forecast ?? []).map((d) => (
                  <div
                    key={d.label}
                    className={cn("forecast-day", d.today && "today")}
                  >
                    <div className="forecast-day-label">{d.label}</div>
                    <div>{d.hours}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {data.show_equipment ? (
            <div className="card">
              <h2 className="section-title">Equipment</h2>
              <div className="equipment-grid">
                {(data.equipment ?? []).map((e) => (
                  <div key={e.label} className="equip">
                    <div className="name">{e.label}</div>
                    <div className="val">{e.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {(data.transients?.length ?? 0) > 0 ? (
        <TransientsPanel env={env} rows={data.transients ?? []} />
      ) : null}
    </>
  );
}

function SimShell({ children }: { children: ReactNode }) {
  return (
    <div className="sim-shell">
      <SimSidebar />
      <main className="sim-main shell">{children}</main>
    </div>
  );
}

function AppRoutes({ env }: { env: EnvName }) {
  const routes = (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route index element={<DashboardPage env={env} />} />
        <Route path="section/:code" element={<SectionPage env={env} />} />
        <Route path="brief" element={<BriefPage env={env} />} />
        <Route path="ops" element={<OperationsPage env={env} />} />
        {env === "sim" ? (
          <Route path="control" element={<Navigate to="/sim" replace />} />
        ) : null}
      </Routes>
    </Suspense>
  );

  if (env === "sim") {
    return <SimShell>{routes}</SimShell>;
  }

  return <div className="shell">{routes}</div>;
}

export function App({ env }: { env: EnvName }) {
  useBoardEvents(env);
  return (
    <>
      <HashScroll />
      <EnvBanner env={env} />
      <AppHeader env={env} />
      <BoardAlertBar env={env} />
      <AppRoutes env={env} />
    </>
  );
}
